import { VIEW_MODE, LOGIN_CUSTOMER } from '@/common/define/common.define';
import { ROUTE_NAME_DASHBOARD } from '@/dashboard/router/routes';
import { setUserLanguage } from '@/common/locale';
import type { ViewMode } from '@/common/stores/view-mode';
import { webStorageController } from '@/common/utils/browser';
import { LoginCustomer } from '@/login/utils/types';
import { LocationQueryValue, RouteLocationNormalized } from 'vue-router';

/**
 * 언어 설정
 */
export const settingLanguage = () => {
  const storedLanguage = webStorageController.getItem({
    type: 'local',
    key: 'language',
  });

  if (storedLanguage) {
    setUserLanguage(storedLanguage);
  }
};

/**
 * viewMode 조회
 */
export const getViewModeByProperty = (viewMode: ViewMode): ViewMode => {
  const sessionStorageViewMode = webStorageController.getItem({
    type: 'session',
    key: 'viewmode',
  });

  let resultViewMode = viewMode;

  if (sessionStorageViewMode && VIEW_MODE[sessionStorageViewMode.toUpperCase()]) {
    resultViewMode = VIEW_MODE[sessionStorageViewMode.toUpperCase()];
  }

  return resultViewMode;
};

/**
 * body 클래스 설정
 */
export const setBodyClass = (mode: ViewMode, customer: string) => {
  const sessionStorageViewMode = webStorageController.getItem({
    type: 'session',
    key: 'viewmode',
  });

  document.body.removeAttribute('class');
  document.body.classList.add(
    `view-mode-${sessionStorageViewMode || mode}`,
    `login-customer-${customer.toLowerCase()}`,
  );
};

/**
 * 메타 타이틀 설정
 */
const setMetaTitle = (productName: string, loginCustomer: LoginCustomer) => {
  const metaTitle = document.querySelector('title') as HTMLTitleElement;
  const titleMap = {
    [LOGIN_CUSTOMER.LGE]: 'DPM',
    [LOGIN_CUSTOMER.MONIMO]: 'monimo exemONE',
  } as const;
  const title = titleMap[loginCustomer] ?? productName;

  metaTitle.textContent = `${title}`;
};

/**
 * 메타 파비콘 설정
 */
const setMetaFavicon = (viewMode: ViewMode) => {
  const faviconLink = document.querySelector("link[rel='icon']") as HTMLLinkElement;
  faviconLink.href = `/favicon_${viewMode}.svg`;
};

/**
 * 메타 정보 설정 (title, favicon)
 */
export const setMetaInfo = (
  productName: string,
  loginCustomer: LoginCustomer,
  viewMode: ViewMode,
) => {
  setMetaTitle(productName, loginCustomer);
  setMetaFavicon(viewMode);
};

/**
 * query 파라미터 변환
 */
export const convertQueryToParams = (query: Record<string, any>): Record<string, any> => {
  const result: Record<string, any> = {};
  Object.keys(query).forEach((key) => {
    if (typeof query[key] === 'string') {
      const rawValue = query[key];
      const normalizedValue = rawValue.toLowerCase();
      if (normalizedValue === 'true' || normalizedValue === 'false') {
        result[key] = normalizedValue === 'true';
      } else if (rawValue !== '' && !Number.isNaN(Number(rawValue))) {
        result[key] = Number(rawValue);
      } else {
        result[key] = rawValue;
      }
    }
  });
  return result;
};

/**
 * 대시보드 뷰 또는 에디터 라우트인지 확인
 * @param to RouteLocationNormalized
 * @returns boolean
 */
/**
 * Vue Router / qs 파싱 특성상 동일 쿼리 키가 반복되면 `string[]`이 될 수 있다.
 * 첫 번째 값만 사용한다.
 */
export const normalizeQueryStringParam = (
  value: LocationQueryValue | LocationQueryValue[] | undefined,
): string | undefined => {
  if (Array.isArray(value)) {
    return value[0] ?? undefined;
  }
  return value ?? undefined;
};

/**
 * 리다이렉트 가능한 경로인지 검증 (open redirect 방지)
 */
export const isRedirectablePath = (path: string | undefined): path is string =>
  !!path &&
  path !== '/logout' &&
  path !== '/login' &&
  path !== '/' &&
  path.startsWith('/') &&
  !path.startsWith('//') &&
  !path.startsWith('/\\');

export const isDashboardRoute = (to: RouteLocationNormalized) => {
  return to.matched.some(
    (record) =>
      record.name !== undefined &&
      (record.name === ROUTE_NAME_DASHBOARD.VIEW || record.name === ROUTE_NAME_DASHBOARD.EDITOR),
  );
};
